<template>
  <section class="company">
    <ModalsShare
      :share-url="currentRoute"
      :title="company.name"
      :showing="modals.share"
      analytics-prefix="CompanyPage"
      @update:showing="modals.share = $event"
    />
    <div class="company-header flex justify-between items-center">
      <h1 class="company-name flex items-center m-0 font-normal tracking-normal text-2xl leading-none lg_text-5xl">
        {{ company.name }}
        <MarkLogo class="h-5 flex-shrink-0 lg_h-7 ml-4 lg_ml-8" />
      </h1>
      <div class="company-buttons hidden md_flex">
        <VButton data-testid="company-page-share-btn" size="lg" @click="modals.share = true">
          <div class="flex justify-evenly items-center">
            <ArrowUpTrayIcon class="w-6 mr-2" /><span class="font-medium text-sm">Share</span>
          </div>
        </VButton>
        <VButton class="mx-2" data-testid="company-page-follow-toggle" size="lg" @click="onFollowToggle">
          <div class="flex justify-evenly items-center">
            <HeartIconSolid v-if="isFollowingCompany" class="w-6 mr-2" />
            <HeartIconOutline v-else class="w-6 mr-2" /><span class="font-med text-sm">{{
              isFollowingCompany ? 'Following' : 'Follow'
            }}</span>
          </div>
        </VButton>
      </div>
      <button
        class="company-buttons-ellipsis inline-block relative cursor-pointer appearance-none bg-white md_hidden"
        @click="ctaDropdown = !ctaDropdown"
      >
        <div class="flex rounded-full border border-black border-gray-400 p-2 mr-2 h-10 w-10">
          <EllipsisVerticalIcon class="w-full h-full" />
        </div>
        <div
          v-show="ctaDropdown"
          class="absolute flex flex-col fixed right-0 min-w-48 border border-gray-300 rounded-md shadow-md mt-2 z-30 bg-white transition-all duration-300 mr-2"
        >
          <ul class="mb-0 text-left">
            <li class="my-2 px-4">
              <button class="appearance-none p-0 bg-transparent" @click="modals.share = true">
                <div class="flex justify-evenly items-center">
                  <ArrowUpTrayIcon class="w-6 mr-4" /><span class="font-normal text-sm">Share</span>
                </div>
              </button>
            </li>
            <li class="mb-2 px-4">
              <button type="button" class="appearance-none bg-white flex justify-evenly items-center p-0" @click="onFollowToggle">
                <HeartIconSolid v-if="isFollowingCompany" class="w-6 mr-4" />
                <HeartIconOutline v-else class="w-6 mr-4" /><span class="text-sm font-normal">{{
                  isFollowingCompany ? 'Following' : 'Follow'
                }}</span>
              </button>
            </li>
          </ul>
        </div>
      </button>
    </div>
    <div class="company-info flex gap-x-4 flex-wrap sm_flex-nowrap text-gray-700 text-sm mt-4 mb-10 lg_text-lg lg_mt-6">
      <div v-if="company.commodity" class="resource flex gap-1 items-start mb-4" data-testid="company-commodity">
        <CircleStackIcon class="w-6 flex-shrink-0" />
        <ul>
          <li v-for="commodity in commodities" :key="commodity.id">{{ commodity.name }}</li>
        </ul>
      </div>
      <div v-if="company.region" class="location flex gap-1 items-start mb-4" data-testid="company-region">
        <MapPinIcon class="w-6 flex-shrink-0" />
        <ul>
          <li v-for="region in regions" :key="region.id">{{ region.name }}</li>
        </ul>
      </div>
      <div v-if="company.last_quote" class="value flex gap-1 items-start">
        <CurrencyDollarIcon class="w-6 flex-shrink-0" />
        <p>${{ formatNumber(company.last_quote.market_cap) }}M</p>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { HeartIcon as HeartIconOutline, EllipsisVerticalIcon, ArrowUpTrayIcon } from '@heroicons/vue/24/outline'
import { HeartIcon as HeartIconSolid, CircleStackIcon, CurrencyDollarIcon, MapPinIcon } from '@heroicons/vue/24/solid'
import { formatNumber } from '@/lib/companyPageHelpers'
import type { Company, IncludeCommodity, IncludeLastQuote, IncludeRegion } from '@/entities/company'
import type { AssetRegion, Commodity } from '@/entities/asset'
import MarkLogo from '~/assets/images/mark-logo.svg'

const props = defineProps({
  company: {
    type: Object as PropType<Company & IncludeCommodity & IncludeRegion & IncludeLastQuote>,
    required: true,
  },
  commodities: {
    type: Array as PropType<DeepReadonly<Commodity[]>>,
    default: () => [],
  },
  regions: {
    type: Array as PropType<DeepReadonly<AssetRegion[]>>,
    default: () => [],
  },
})

const { $analytics } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const { state: user, setFollowCompany } = useUser()
const modals = reactive({ share: false })
const ctaDropdown = ref(false)

const isFollowingCompany = computed(() => {
  return user.value.followingCompanies.some(({ id }) => id === props.company.id)
})

const currentRoute = computed(() => {
  if (process.server) return ''
  return window.location.origin + window.location.pathname
})

const onFollowToggle = async () => {
  if (!user.value.user) {
    window.location.href = `/login?redirectedFrom=${window.location.pathname}&followToggled=true`
  } else {
    await setFollowCompany({ action: isFollowingCompany.value ? 'unfollow' : 'follow', id: props.company.id })

    const analyticsMetaData = {
      companyName: props.company.name,
      companyId: props.company.id,
    }

    isFollowingCompany.value
      ? $analytics.track('companiesCompanyUnfollowed', analyticsMetaData)
      : $analytics.track('companiesCompanyFollowed', analyticsMetaData)
  }
}

onMounted(() => {
  if (route.query.followToggled) {
    onFollowToggle()
    router.replace({ query: {} })
  }
})
</script>

<style lang="stylus">
.resource, .location
  ul li
    @apply inline whitespace-nowrap
    &::after
      content: ', '
      @apply whitespace-normal
    &:last-child::after
      content: none
</style>
